import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, Transition as _Transition } from "vue";
const _hoisted_1 = {
  key: 0,
  "data-cy": "fixed-fee-edit-panel",
  class: "fixed top-20 right-0 bg-white shadow-2xl z-50 flex flex-col rounded-lg overflow-hidden",
  style: {
    "width": "40vw",
    "min-width": "500px",
    "max-width": "800px",
    "height": "calc(100vh - 80px)"
  }
};
const _hoisted_2 = {
  class: "flex items-center justify-between px-6 py-3 bg-white"
};
const _hoisted_3 = {
  key: 0,
  class: "flex items-center gap-3 flex-1 min-w-0 flex-wrap"
};
const _hoisted_4 = {
  key: 1,
  class: "flex items-center gap-3 flex-1 min-w-0 flex-wrap"
};
const _hoisted_5 = {
  class: "text-lg font-bold text-gray-900 truncate"
};
const _hoisted_6 = {
  key: 0,
  class: "flex items-center gap-1"
};
const _hoisted_7 = {
  class: "px-2 py-1 bg-gray-100 rounded text-sm font-medium text-gray-700 cursor-pointer"
};
const _hoisted_8 = {
  class: "px-2 py-1 bg-gray-100 rounded text-sm font-medium text-gray-700 cursor-pointer"
};
const _hoisted_9 = {
  key: 2,
  class: "flex items-center gap-2 flex-shrink-0 ml-4"
};
const _hoisted_10 = {
  key: 0,
  class: "px-6 py-1 border-b border-gray-200"
};
const _hoisted_11 = {
  key: 1,
  class: "px-6 py-0 border-b border-gray-200"
};
const _hoisted_12 = {
  class: "text-sm text-gray-600"
};
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import CreateChargePanel from './components/CreateChargePanel.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { useChargesStore } from '@/services/UserService/useChargesStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import { formatPercentage, getProfitabilityBadgeClass, getProfitabilityIcon } from './formatters';
import { formatCurrency } from '@/services/utils';
export default {
  __name: 'FixedFeeEditPanel',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    chargeId: {
      type: [String, Number],
      default: null
    },
    matterId: {
      type: [String, Number],
      default: null
    },
    state: {
      type: Object,
      required: false
    },
    stateParams: {
      type: Object,
      required: false
    },
    rootScope: {
      type: Object,
      required: false
    },
    alerts: {
      type: Object,
      required: false
    },
    snippetService: {
      type: Object,
      required: false
    },
    entryEditService: {
      type: Object,
      required: false
    },
    tracking: {
      type: Object,
      required: false
    },
    entryPoint: {
      type: String,
      required: false,
      default: 'fixed_fee_list'
    },
    clientMatterManager: {
      type: Object,
      required: false
    }
  },
  emits: ['update:modelValue', 'saved', 'savedAndContinue', 'closed'],
  setup(__props, {
    expose: __expose,
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;
    const createChargeRef = ref(null);
    const chargesStore = useChargesStore();
    const meStore = useMeStore();
    const {
      permissions
    } = storeToRefs(meStore);
    const chargeData = ref(null);
    const isLoading = ref(true);
    const canViewRates = computed(() => permissions.value?.time?.viewRates || false);

    // Computed stateParams for CreateCharge (merge with passed stateParams)
    const computedStateParams = computed(() => ({
      ...props.stateParams,
      chargeId: props.chargeId,
      matterId: props.matterId,
      fixedFeeTemplate: false,
      ledes: false,
      showAllocation: false
    }));
    // Handle charge loaded event from CreateChargePanel (single source of truth)
    const handleChargeLoaded = charge => {
      // Update header when CreateCharge loads the data
      if (charge) {
        // If this is a partial update (e.g. from matter change) that has an id
        // but no amount data, only update the labels — preserve existing metrics
        const isPartialUpdate = charge.id && charge.amount === undefined && chargeData.value?.metrics;
        if (isPartialUpdate) {
          chargeData.value = {
            ...chargeData.value,
            clientLabel: charge.matter?.client?.name || charge.clientLabel || chargeData.value.clientLabel,
            matterLabel: charge.matter?.name || charge.matterLabel || chargeData.value.matterLabel
          };
          isLoading.value = false;
          return;
        }

        // Calculate timeCost from profitability: timeCost = amount - profitabilityAmount
        const amount = charge.amount || 0;
        const profitabilityAmount = charge.profitabilityAmount || 0;
        const timeCost = amount - profitabilityAmount;
        chargeData.value = {
          clientLabel: charge.matter?.client?.name || charge.clientLabel || '',
          matterLabel: charge.matter?.name || charge.matterLabel || 'Fixed Fee',
          billedState: charge.billedState || 'WIP',
          metrics: charge.id ? {
            amount: amount,
            timeCost: timeCost,
            profitabilityAmount: profitabilityAmount,
            profitabilityPercentage: charge.profitabilityPercentage || 0
          } : null
        };
      } else {
        chargeData.value = {
          clientLabel: 'New Fixed Fee',
          matterLabel: '',
          billedState: null,
          metrics: null
        };
      }
      isLoading.value = false;
    };

    // Clear data when panel closes and reset loading state when opening
    watch(() => props.modelValue, isOpen => {
      if (!isOpen) {
        chargeData.value = null;
        isLoading.value = true;
      } else {
        isLoading.value = true;
      }
    });

    // Reset loading state when switching to a different fixed fee while panel is open
    watch(() => props.chargeId, (newChargeId, oldChargeId) => {
      if (props.modelValue && newChargeId !== oldChargeId) {
        isLoading.value = true;
        chargeData.value = null;
      }
    });
    const handleClose = async () => {
      // Check for unsaved changes before closing
      const shouldProceed = await confirmDiscardChanges('closing the panel');
      if (!shouldProceed) {
        return; // User chose to keep editing
      }
      emit('update:modelValue', false);
      emit('closed');
    };
    const handleSaved = fixedFee => {
      emit('saved', fixedFee);
      emit('update:modelValue', false);
    };

    // Handle save-and-continue: reload table but don't close panel (panel close is handled by openPanel)
    const handleSavedAndContinue = () => {
      emit('savedAndContinue');
    };

    /**
     * Confirm whether to proceed when there are unsaved changes
     * Delegates to the CreateChargePanel's confirmation logic
     * @param {String} action - Description of the action being performed
     * @returns {Promise<Boolean>} - true if should proceed, false if should stay
     */
    const confirmDiscardChanges = async (action = 'selecting another fixed fee') => {
      if (createChargeRef.value?.confirmDiscardChanges) {
        return await createChargeRef.value.confirmDiscardChanges(action);
      }
      return true; // No ref available, proceed
    };

    /**
     * Check if form has unsaved changes
     * @returns {Boolean}
     */
    const hasUnsavedChanges = () => {
      if (createChargeRef.value?.hasUnsavedChanges) {
        return createChargeRef.value.hasUnsavedChanges();
      }
      return false;
    };

    // Expose methods for parent component to call
    __expose({
      confirmDiscardChanges,
      hasUnsavedChanges
    });
    return (_ctx, _cache) => {
      const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
      const _component_v_icon = _resolveComponent("v-icon");
      return _openBlock(), _createBlock(_Transition, {
        name: "slide-panel",
        "enter-active-class": "slide-panel-enter-active",
        "leave-active-class": "slide-panel-leave-active",
        "enter-from-class": "slide-panel-enter-from",
        "enter-to-class": "slide-panel-enter-to",
        "leave-from-class": "slide-panel-leave-from",
        "leave-to-class": "slide-panel-leave-to"
      }, {
        default: _withCtx(() => [__props.modelValue ? (_openBlock(), _createElementBlock("div", _hoisted_1, [_createElementVNode("div", _hoisted_2, [isLoading.value ? (_openBlock(), _createElementBlock("div", _hoisted_3, [_createVNode(_component_v_skeleton_loader, {
          type: "text",
          width: "200",
          class: "d-inline-block"
        }), _createVNode(_component_v_skeleton_loader, {
          type: "chip",
          width: "80",
          class: "d-inline-block"
        }), _createVNode(_component_v_skeleton_loader, {
          type: "chip",
          width: "80",
          class: "d-inline-block"
        }), _createVNode(_component_v_skeleton_loader, {
          type: "chip",
          width: "120",
          class: "d-inline-block"
        })])) : (_openBlock(), _createElementBlock("div", _hoisted_4, [_createElementVNode("h2", _hoisted_5, _toDisplayString(chargeData.value?.clientLabel || 'New Fixed Fee'), 1), chargeData.value?.metrics ? (_openBlock(), _createElementBlock("div", _hoisted_6, [_createVNode(LlTooltip, {
          text: "Fixed Fee Amount",
          location: "bottom"
        }, {
          activator: _withCtx(() => [_createElementVNode("span", _hoisted_7, _toDisplayString(_unref(formatCurrency)(chargeData.value.metrics.amount)), 1)]),
          _: 1
        }), canViewRates.value ? (_openBlock(), _createBlock(LlTooltip, {
          key: 0,
          text: "Total Time Cost",
          location: "bottom"
        }, {
          activator: _withCtx(() => [_createElementVNode("span", _hoisted_8, " Cost: " + _toDisplayString(_unref(formatCurrency)(chargeData.value.metrics.timeCost)), 1)]),
          _: 1
        })) : _createCommentVNode("", true), canViewRates.value ? (_openBlock(), _createBlock(LlTooltip, {
          key: 1,
          text: "Profitability (Amount - Cost)",
          location: "bottom"
        }, {
          activator: _withCtx(() => [_createElementVNode("span", {
            class: _normalizeClass(["px-2 py-1 rounded text-sm font-medium cursor-pointer flex items-center gap-1", _unref(getProfitabilityBadgeClass)(chargeData.value.metrics.profitabilityPercentage)])
          }, [_createVNode(_component_v_icon, {
            size: "12"
          }, {
            default: _withCtx(() => [_createTextVNode(_toDisplayString(_unref(getProfitabilityIcon)(chargeData.value.metrics.profitabilityPercentage)), 1)]),
            _: 1
          }), _createTextVNode(" " + _toDisplayString(_unref(formatCurrency)(chargeData.value.metrics.profitabilityAmount)) + " (" + _toDisplayString(_unref(formatPercentage)(chargeData.value.metrics.profitabilityPercentage)) + ") ", 1)], 2)]),
          _: 1
        })) : _createCommentVNode("", true)])) : _createCommentVNode("", true)])), !isLoading.value ? (_openBlock(), _createElementBlock("div", _hoisted_9, [_createVNode(LlSecondaryButton, {
          icon: "mdi-close",
          variant: "text",
          size: "small",
          "aria-label": "Close",
          "data-cy": "close-panel-btn",
          onClick: handleClose
        })])) : _createCommentVNode("", true)]), isLoading.value ? (_openBlock(), _createElementBlock("div", _hoisted_10, [_createVNode(_component_v_skeleton_loader, {
          type: "text",
          width: "250"
        })])) : chargeData.value?.matterLabel ? (_openBlock(), _createElementBlock("div", _hoisted_11, [_createElementVNode("p", _hoisted_12, _toDisplayString(chargeData.value.matterLabel), 1)])) : _createCommentVNode("", true), (_openBlock(), _createBlock(CreateChargePanel, {
          key: `fixed-fee-${__props.chargeId || __props.matterId || 'new'}`,
          state: __props.state,
          stateParams: computedStateParams.value,
          rootScope: __props.rootScope,
          alerts: __props.alerts,
          snippetService: __props.snippetService,
          entryEditService: __props.entryEditService,
          tracking: __props.tracking,
          clientMatterManager: __props.clientMatterManager,
          entryPoint: __props.entryPoint,
          onClose: handleClose,
          onSaved: handleSaved,
          onSavedAndContinue: handleSavedAndContinue,
          onChargeLoaded: handleChargeLoaded,
          ref_key: "createChargeRef",
          ref: createChargeRef,
          class: "flex-1 overflow-auto panel-content"
        }, null, 8, ["state", "stateParams", "rootScope", "alerts", "snippetService", "entryEditService", "tracking", "clientMatterManager", "entryPoint"]))])) : _createCommentVNode("", true)]),
        _: 1
      });
    };
  }
};